/**
 * PlayersNearYouTeaser — public-facing card that shows 2–3 anonymized
 * players already on the platform, with a "Verify to message" CTA.
 *
 * Why: lets prospects feel the value of Verified Player (you can
 * actually message real people near you) without revealing any
 * personal info to non-paying users.
 *
 * Privacy guarantees (also enforced server-side at /api/players/preview):
 *   - first initial only (no full name)
 *   - city + state only (no zip, no street, no exact location)
 *   - no photo, email, or phone
 *   - excludes `hide_from_search`, banned, demo, test, admin accounts
 *
 * The component is silent (renders nothing) when:
 *   - the API returns 0 players (e.g. fresh DB, or backend down)
 *   - the current user is already a paid tier (they have the real
 *     search page — no need for a teaser)
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { isPaidTier } from '@/lib/tiers';
import { Button } from '@/components/ui/button';
import { Users, MapPin, ArrowRight } from 'lucide-react';

const SKILL_LABELS = {
  all: 'All levels',
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};

export default function PlayersNearYouTeaser({ zipCode = '', className = '' }) {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchPreview = async () => {
      try {
        const params = new URLSearchParams();
        if (zipCode) params.set('zip_code', zipCode);
        params.set('limit', '3');
        const res = await api.get(`/players/preview?${params.toString()}`);
        if (!cancelled) {
          setPlayers(res.data?.players || []);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) setLoaded(true);
      }
    };
    fetchPreview();
    return () => { cancelled = true; };
  }, [zipCode]);

  // Paid users already have the real search page — no point teasing them.
  if (user && isPaidTier(user)) return null;
  if (!loaded || players.length === 0) return null;

  const isLoggedIn = !!user;
  const ctaHref = isLoggedIn ? '/subscription' : '/register?tier=verified_player';
  const ctaLabel = isLoggedIn ? 'Verify to start messaging' : 'Verify to message →';
  const headline = zipCode
    ? `Players near ${zipCode.slice(0, 5)}`
    : 'Players already on Meet for Mahjong';

  return (
    <section
      data-testid="players-near-you-teaser"
      className={`rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-[#56dfb4]/5 p-6 sm:p-8 ${className}`}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-[#56dfb4]/15 flex items-center justify-center">
          <Users className="w-5 h-5 text-[#1f8a6a]" />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {headline}
        </h2>
      </div>
      <p className="text-sm text-slate-600 mb-5">
        Real players in your area — message them directly to set up casual games or coordinate
        at upcoming events. Identities stay private until both of you connect.
      </p>

      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        {players.map((p, i) => (
          <div
            key={i}
            data-testid={`players-near-you-tile-${i}`}
            className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-col gap-2"
          >
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-[#b456df]/15 text-[#b456df] flex items-center justify-center font-semibold">
                {p.first_initial}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 truncate">{p.first_initial}</p>
                <p className="text-xs text-slate-500">{SKILL_LABELS[p.skill_level] || 'All levels'}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{p.city}, {p.state}</span>
            </div>
            <div className="mt-1 pt-2 border-t border-slate-100">
              <p className="text-xs text-slate-400 italic">🔒 Verify to see full name &amp; message</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          <strong>Verified Player</strong> unlocks messaging + daily new-event alerts within {' '}
          your chosen radius. Starts at <strong>$0.99/mo</strong>.
        </p>
        <Link to={ctaHref} data-testid="players-near-you-cta">
          <Button className="rounded-full bg-[#b456df] hover:bg-[#9a3ec4] text-white gap-2 px-5">
            {ctaLabel} <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </div>
    </section>
  );
}
